import { Link, useNavigate } from "react-router-dom";
import { Search, ShoppingCart, User, LogOut, LayoutDashboard, MessageCircle, Bell, Globe, Heart, Languages, Menu, X, ShoppingBag } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { useCart } from "@/lib/cart";
import { useCurrency, COUNTRIES } from "@/lib/currency";
import { useLang, LANGUAGES } from "@/lib/i18n";
import { useState, useEffect, useCallback } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuTrigger, DropdownMenuSeparator, DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";

export default function Header() {
  const { user, logout } = useAuth();
  const { count } = useCart();
  const { country, currency, setCountry } = useCurrency();
  const { lang, setLang, t } = useLang();
  const nav = useNavigate();
  const [q, setQ] = useState("");
  const [mobileOpen, setMobileOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const currentCountry = COUNTRIES.find((c) => c.code === country) || COUNTRIES[0];
  const currentLang = LANGUAGES.find((l) => l.code === lang) || LANGUAGES[0];

  const refreshUnread = useCallback(async () => {
    if (!user) return;
    try {
      const { data } = await api.get("/notifications/unread-count");
      setUnreadCount(data?.count || 0);
    } catch { /* silent */ }
  }, [user]);

  // Real-time notifications via Server-Sent Events (falls back to polling if SSE fails)
  useEffect(() => {
    if (!user) { setUnreadCount(0); return; }
    refreshUnread();

    const backendURL = process.env.REACT_APP_BACKEND_URL || "";
    // The httpOnly session cookie is sent automatically on same-origin EventSource
    // requests when `withCredentials` is true (Feb 2026 cookie migration).
    const es = new EventSource(`${backendURL}/api/notifications/stream`, {
      withCredentials: true,
    });

    es.addEventListener("unread_count", (e) => {
      try { const j = JSON.parse(e.data); setUnreadCount(j.count || 0); } catch { /* ignore */ }
    });
    es.addEventListener("notification", (e) => {
      // A new notification just came in — bump the count and broadcast
      setUnreadCount((c) => c + 1);
      try {
        const notif = JSON.parse(e.data);
        window.dispatchEvent(new CustomEvent("sukana:notification", { detail: notif }));
      } catch { /* ignore */ }
    });

    let pollFallbackId = null;
    es.onerror = () => {
      // Connection dropped or blocked — fall back to periodic polling
      if (!pollFallbackId) pollFallbackId = setInterval(refreshUnread, 60_000);
    };

    return () => {
      es.close();
      if (pollFallbackId) clearInterval(pollFallbackId);
    };
  }, [user, refreshUnread]);

  const onSearch = (e) => {
    e.preventDefault();
    if (q.trim()) nav(`/recherche?q=${encodeURIComponent(q.trim())}`);
  };

  return (
    <header className="sticky top-0 z-40 bg-white/85 backdrop-blur-2xl border-b border-black/5" data-testid="site-header">
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 py-4 flex items-center gap-3 sm:gap-6">
        <Link to="/" className="flex items-center gap-2 shrink-0" data-testid="logo-link">
          <img
            src="https://customer-assets-4nw71qhi.emergentagent.net/job_sukana-launch/artifacts/9b5fhq23_LOGO%20SUKACOOP.PNG"
            alt="SUKANACOOP"
            className="w-10 h-10 sm:w-11 sm:h-11 object-contain"
          />
          <span className="heading font-extrabold text-base sm:text-xl tracking-tight text-[#1A3322] hidden sm:inline">SUKANACOOP</span>
        </Link>

        <form onSubmit={onSearch} className="hidden md:flex flex-1 max-w-2xl relative" data-testid="search-form">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[#4B5563]" />
          <Input
            placeholder={t("header.search")}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="pl-11 pr-4 h-11 rounded-full border-[#1A3322]/15 bg-[#F5F5F0] focus:bg-white focus:border-[#1A3322]"
            data-testid="search-input"
          />
        </form>

        <nav className="hidden lg:flex items-center gap-6 text-sm">
          <Link to="/produits" className="text-[#111812] hover:text-[#C85A32]" data-testid="nav-products">{t("header.products")}</Link>
          <Link to="/services" className="text-[#111812] hover:text-[#C85A32]" data-testid="nav-services">{t("header.services")}</Link>
          <Link to="/boutiques" className="text-[#111812] hover:text-[#C85A32]" data-testid="nav-shops">{t("dir.header_link")}</Link>
          <Link to="/personal-shopper" className="text-[#111812] hover:text-[#C85A32] whitespace-nowrap flex items-center gap-1" data-testid="nav-personal-shopper">
            <span className="w-1.5 h-1.5 rounded-full bg-[#C85A32]"/>{t("nav.personal_shopper")}
          </Link>
          <Link to="/categories" className="text-[#111812] hover:text-[#C85A32]" data-testid="nav-categories">{t("header.categories")}</Link>
        </nav>

        <div className="flex items-center gap-1.5 sm:gap-2 ml-auto shrink-0">
          <button
            onClick={() => setMobileOpen((v) => !v)}
            className="lg:hidden p-2 rounded-full hover:bg-[#F5F5F0]"
            aria-label="Menu"
            data-testid="mobile-menu-btn"
          >
            {mobileOpen ? <X className="w-5 h-5 text-[#1A3322]"/> : <Menu className="w-5 h-5 text-[#1A3322]"/>}
          </button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button data-testid="lang-selector" className="flex items-center gap-1 px-2 sm:px-3 py-2 rounded-full bg-[#F5F5F0] hover:bg-[#E8E8E0] text-sm font-medium" aria-label="Change language">
                <Languages className="w-4 h-4 text-[#1A3322]" />
                <span className="hidden md:inline text-xs label-uppercase">{currentLang.code}</span>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-44">
              <DropdownMenuLabel className="flex items-center gap-2"><Languages className="w-3 h-3"/> {t("header.language")}</DropdownMenuLabel>
              <DropdownMenuSeparator />
              {LANGUAGES.map((l) => (
                <DropdownMenuItem key={l.code} onClick={() => setLang(l.code)} data-testid={`lang-${l.code}`}>
                  <span className="text-lg mr-2">{l.flag}</span>
                  <span className="flex-1">{l.name}</span>
                  {lang === l.code && <span className="text-[10px] font-bold text-[#1A3322]">✓</span>}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button data-testid="currency-selector" className="flex items-center gap-1.5 px-2 sm:pl-3 sm:pr-3 py-2 rounded-full bg-[#F5F5F0] hover:bg-[#E8E8E0] text-sm font-medium">
                <span className="text-base sm:text-lg leading-none">{currentCountry.flag}</span>
                <span className="hidden sm:inline text-xs label-uppercase">{currency}</span>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel className="flex items-center gap-2"><Globe className="w-3 h-3"/> {t("header.country_currency")}</DropdownMenuLabel>
              <DropdownMenuSeparator />
              {COUNTRIES.map((c) => (
                <DropdownMenuItem key={c.code} onClick={() => setCountry(c.code)} data-testid={`country-${c.code}`}>
                  <span className="text-lg mr-2">{c.flag}</span>
                  <span className="flex-1">{c.name}</span>
                  <span className="text-xs text-[#4B5563] label-uppercase">{c.currency}</span>
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          <Link to="/panier" className="relative p-2" data-testid="cart-link">
            <ShoppingCart className="w-5 h-5 text-[#1A3322]" />
            {count > 0 && (
              <span data-testid="cart-count" className="absolute -top-1 -right-1 bg-[#C85A32] text-white text-[10px] rounded-full w-5 h-5 flex items-center justify-center font-bold">{count}</span>
            )}
          </Link>

          {user ? (
            <>
              <Link to="/notifications" className="relative p-2 hidden sm:block" data-testid="notifications-link" title={t("header.notifications")}>
                <Bell className="w-5 h-5 text-[#1A3322]" />
                {unreadCount > 0 && (
                  <span data-testid="notif-badge" className="absolute -top-0.5 -right-0.5 bg-[#C85A32] text-white text-[10px] rounded-full min-w-[18px] h-[18px] px-1 flex items-center justify-center font-bold">
                    {unreadCount > 9 ? "9+" : unreadCount}
                  </span>
                )}
              </Link>
              <Link to="/messagerie" className="p-2 hidden sm:block" data-testid="messages-link">
                <MessageCircle className="w-5 h-5 text-[#1A3322]" />
              </Link>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button data-testid="user-menu-trigger" className="flex items-center gap-2 pl-2 pr-3 sm:pl-3 sm:pr-4 py-2 rounded-full bg-[#F5F5F0] hover:bg-[#E8E8E0]">
                    <div className="w-7 h-7 rounded-full bg-[#1A3322] text-white text-xs font-bold flex items-center justify-center">
                      {user.full_name?.[0]?.toUpperCase() || "U"}
                    </div>
                    <span className="hidden sm:block text-sm font-medium">{user.full_name?.split(" ")[0]}</span>
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuLabel>{user.email}</DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem asChild><Link to="/profil" data-testid="menu-profile"><User className="w-4 h-4 mr-2"/>{t("header.profile")}</Link></DropdownMenuItem>
                  <DropdownMenuItem asChild><Link to="/favoris" data-testid="menu-favorites"><Heart className="w-4 h-4 mr-2"/>{t("header.favorites")}</Link></DropdownMenuItem>
                  <DropdownMenuItem asChild><Link to="/notifications" data-testid="menu-notifications"><Bell className="w-4 h-4 mr-2"/>{t("header.notifications")}{unreadCount > 0 && <span className="ml-auto text-[10px] bg-[#C85A32] text-white rounded-full px-2 py-0.5 font-bold">{unreadCount}</span>}</Link></DropdownMenuItem>
                  <DropdownMenuItem asChild><Link to="/commandes" data-testid="menu-orders">{t("header.orders")}</Link></DropdownMenuItem>
                  {(user.role === "seller" || user.role === "admin") && (
                    <DropdownMenuItem asChild><Link to="/vendeur" data-testid="menu-seller"><LayoutDashboard className="w-4 h-4 mr-2"/>{t("header.seller_dashboard")}</Link></DropdownMenuItem>
                  )}
                  {user.role === "admin" && (
                    <DropdownMenuItem asChild><Link to="/admin" data-testid="menu-admin"><LayoutDashboard className="w-4 h-4 mr-2"/>{t("header.admin")}</Link></DropdownMenuItem>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => { logout(); nav("/"); }} data-testid="menu-logout">
                    <LogOut className="w-4 h-4 mr-2"/>{t("header.logout")}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </>
          ) : (
            <>
              <Link to="/connexion" className="hidden sm:inline-block"><Button variant="ghost" data-testid="nav-login">{t("header.login")}</Button></Link>
              <Link to="/inscription"><Button className="pill-btn bg-[#1A3322] hover:bg-[#24472F] text-white rounded-full text-sm px-4" data-testid="nav-register">{t("header.register")}</Button></Link>
            </>
          )}
        </div>
      </div>

      {/* Mobile search */}
      <form onSubmit={onSearch} className="md:hidden px-4 pb-3 relative">
        <Search className="absolute left-7 top-1/2 -translate-y-[75%] w-4 h-4 text-[#4B5563]" />
        <Input placeholder={t("header.search.short")} value={q} onChange={(e) => setQ(e.target.value)}
          className="pl-11 h-10 rounded-full bg-[#F5F5F0] border-[#1A3322]/15"
          data-testid="search-input-mobile" />
      </form>

      {/* Mobile navigation drawer */}
      {mobileOpen && (
        <div className="lg:hidden border-t border-black/5 bg-white" data-testid="mobile-nav-panel">
          <nav className="flex flex-col px-4 py-3 divide-y divide-black/5">
            <Link to="/produits" onClick={() => setMobileOpen(false)} className="py-3 text-[15px] font-medium text-[#111812] hover:text-[#C85A32] flex items-center justify-between" data-testid="mobile-nav-products">
              <span>{t("header.products")}</span>
              <span className="text-[#4B5563]">→</span>
            </Link>
            <Link to="/services" onClick={() => setMobileOpen(false)} className="py-3 text-[15px] font-medium text-[#111812] hover:text-[#C85A32] flex items-center justify-between" data-testid="mobile-nav-services">
              <span>{t("header.services")}</span>
              <span className="text-[#4B5563]">→</span>
            </Link>
            <Link to="/boutiques" onClick={() => setMobileOpen(false)} className="py-3 text-[15px] font-medium text-[#111812] hover:text-[#C85A32] flex items-center justify-between" data-testid="mobile-nav-shops">
              <span>{t("dir.header_link")}</span>
              <span className="text-[#4B5563]">→</span>
            </Link>
            <Link to="/personal-shopper" onClick={() => setMobileOpen(false)} className="py-3 text-[15px] font-medium text-[#C85A32] flex items-center justify-between" data-testid="mobile-nav-personal-shopper">
              <span className="flex items-center gap-2"><ShoppingBag className="w-4 h-4"/>{t("nav.personal_shopper")}</span>
              <span className="text-[#4B5563]">→</span>
            </Link>
            <Link to="/categories" onClick={() => setMobileOpen(false)} className="py-3 text-[15px] font-medium text-[#111812] hover:text-[#C85A32] flex items-center justify-between" data-testid="mobile-nav-categories">
              <span>{t("header.categories")}</span>
              <span className="text-[#4B5563]">→</span>
            </Link>
            {!user && (
              <Link to="/connexion" onClick={() => setMobileOpen(false)} className="py-3 text-[15px] font-medium text-[#111812] hover:text-[#C85A32] flex items-center justify-between" data-testid="mobile-nav-login">
                <span>{t("header.login")}</span>
                <span className="text-[#4B5563]">→</span>
              </Link>
            )}
          </nav>
        </div>
      )}
    </header>
  );
}
